// WPS-018 · Font Pairing Panel
// Client picks ONE of 20 curated font pairings · live preview via CardCanvas.
// Wunli AI can suggest the best pairing for the client's industry + tone.
import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Sparkles, Check } from 'lucide-react';
import CardCanvas from './CardCanvas';
import { fontPairingsApi, wunliAgentApi } from '../services/api';

// Preload Google Fonts links lazily (only the families used)
const ensureFontsLoaded = (pairings) => {
  const families = new Set();
  pairings.forEach(p => {
    [p.display, p.body, p.accent, p.contact].filter(Boolean).forEach(s => {
      if (s.family) families.add(s.family);
    });
  });
  const linkId = 'wunli-fonts-link';
  const existing = document.getElementById(linkId);
  if (existing) existing.remove();
  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  const familiesParam = Array.from(families)
    .map(f => f.replace(/ /g, '+') + ':wght@300;400;500;600;700;800;900')
    .join('&family=');
  link.href = `https://fonts.googleapis.com/css2?family=${familiesParam}&display=swap`;
  document.head.appendChild(link);
};

const MOOD_FILTERS = ['all', 'luxury', 'modern', 'classic', 'bold', 'minimal'];

const FontPairingPanel = ({
  selectedSlug,
  onSelect,
  customerCategory = null,
  customerData = {},
  showLivePreview = true,
}) => {
  const [pairings, setPairings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mood, setMood] = useState('all');
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = customerCategory ? { industry: customerCategory } : {};
      const data = await fontPairingsApi.list(params);
      const list = data || [];
      setPairings(list);
      ensureFontsLoaded(list);
      setLoading(false);
    })();
  }, [customerCategory]);

  const filtered = mood === 'all' ? pairings : pairings.filter(p => p.mood === mood);

  const askWunli = async () => {
    setAiLoading(true);
    setAiSuggestion(null);
    const r = await wunliAgentApi.ask('pairing', {
      industry: customerCategory || 'general',
      tone: customerData.tone || 'modern',
    });
    setAiLoading(false);
    // Match suggestion text against pairing names
    const suggestions = r?.suggestions || [];
    setAiSuggestion(suggestions.join(' · '));
  };

  const sampleData = (p) => ({
    companyName: customerData.companyName || 'Your Company',
    personalName: customerData.personalName || 'Carlos Herrera',
    jobTitle: customerData.jobTitle || 'Owner',
    email: customerData.email || 'you@biz.com',
    phone: customerData.phone || '+1 214 949 7999',
    website: customerData.website || 'yourbiz.com',
    slogan: customerData.slogan || 'Reliable repairs · 24/7 service',
    colors: customerData.colors || ['#0a0a0a', '#c9a961'],
    orientation: 'horizontal',
    qrPosition: 'back',
    backDesign: { enabled: false },
  });

  return (
    <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{
            fontSize: '10px', fontFamily: 'JetBrains Mono, monospace',
            letterSpacing: '0.15em', textTransform: 'uppercase',
            color: '#64748b', marginBottom: '4px',
          }}>Step · Typography</div>
          <div style={{ fontSize: '20px', fontWeight: 800, letterSpacing: '-0.02em', color: '#0f172a' }}>
            Choose your font pairing
          </div>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
            Curated pairs · body min 10pt guaranteed · print-safe at 300 DPI
          </div>
        </div>

        <Button onClick={askWunli} disabled={aiLoading} className="bg-amber-600 hover:bg-amber-700 text-white">
          <Sparkles className="h-4 w-4 mr-2" />
          {aiLoading ? 'Asking…' : 'Wunli AI · Suggest for me'}
        </Button>
      </div>

      {aiSuggestion && (
        <div style={{
          background: '#fef3c7', border: '1px solid #fbbf24',
          padding: '12px 14px', marginBottom: '14px',
          fontSize: '13px', color: '#78350f', fontStyle: 'italic',
        }}>
          <strong style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase', display: 'block', marginBottom: '4px', color: '#92400e' }}>
            ✨ Wunli AI says
          </strong>
          {aiSuggestion}
        </div>
      )}

      {/* Mood filter */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap' }}>
        {MOOD_FILTERS.map(m => (
          <button
            key={m}
            onClick={() => setMood(m)}
            style={{
              padding: '6px 12px',
              fontSize: '10px', fontFamily: 'JetBrains Mono, monospace',
              letterSpacing: '0.12em', textTransform: 'uppercase',
              border: '1px solid', borderColor: mood === m ? '#0f172a' : '#e2e8f0',
              background: mood === m ? '#0f172a' : '#ffffff',
              color: mood === m ? '#ffffff' : '#0f172a',
              cursor: 'pointer',
            }}
          >{m === 'all' ? `All · ${pairings.length}` : m}</button>
        ))}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', color: '#64748b' }}>
          Loading curated pairings…
        </div>
      )}

      {/* Pairings grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: '12px',
      }}>
        {filtered.map(p => {
          const isSelected = selectedSlug === p.slug;
          return (
            <div
              key={p.id || p.slug}
              onClick={() => onSelect(p)}
              style={{
                border: `2px solid ${isSelected ? '#01579b' : '#e2e8f0'}`,
                background: '#ffffff',
                cursor: 'pointer',
                padding: '14px',
                position: 'relative',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.borderColor = '#94a3b8'; }}
              onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.borderColor = '#e2e8f0'; }}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{p.name}</div>
                  <div style={{
                    fontSize: '9px', fontFamily: 'JetBrains Mono, monospace',
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: '#64748b', marginTop: '2px',
                  }}>
                    {p.mood} · score {p.qualityScore || 90}/100
                  </div>
                </div>
                {isSelected && (
                  <div style={{
                    width: '20px', height: '20px', background: '#01579b',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Check className="h-3 w-3 text-white" />
                  </div>
                )}
              </div>

              {/* Live type sample */}
              <div style={{ padding: '8px 0', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', marginBottom: '8px' }}>
                <div style={{
                  fontFamily: `'${p.display?.family || 'Inter'}', Inter, sans-serif`,
                  fontWeight: p.display?.weight || 700,
                  fontStyle: p.display?.style || 'normal',
                  fontSize: '22px',
                  letterSpacing: '-0.01em',
                  color: '#0f172a',
                  lineHeight: 1.1,
                }}>Lone Wolf</div>
                <div style={{
                  fontFamily: `'${p.body?.family || 'Inter'}', Inter, sans-serif`,
                  fontWeight: p.body?.weight || 400,
                  fontSize: '13px',
                  color: '#475569',
                  lineHeight: 1.3,
                  marginTop: '2px',
                }}>Carlos Herrera · Owner</div>
                {p.accent && (
                  <div style={{
                    fontFamily: `'${p.accent.family}', Inter, sans-serif`,
                    fontWeight: p.accent.weight || 400,
                    fontStyle: p.accent.style || 'normal',
                    fontSize: '11px',
                    color: '#94a3b8',
                    marginTop: '4px',
                  }}>"Reliable repairs · 24/7"</div>
                )}
              </div>

              {/* Pairing meta */}
              <div style={{
                fontSize: '9px',
                fontFamily: 'JetBrains Mono, monospace',
                color: '#64748b',
                lineHeight: 1.5,
              }}>
                <div>Display · <strong style={{ color: '#0f172a' }}>{p.display?.family}</strong></div>
                <div>Body · <strong style={{ color: '#0f172a' }}>{p.body?.family}</strong></div>
                {p.accent && <div>Accent · <strong style={{ color: '#0f172a' }}>{p.accent.family}</strong></div>}
              </div>
            </div>
          );
        })}
      </div>

      {showLivePreview && selectedSlug && (
        <div style={{
          marginTop: '20px',
          padding: '16px',
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
        }}>
          <div style={{
            fontSize: '10px', fontFamily: 'JetBrains Mono, monospace',
            letterSpacing: '0.15em', textTransform: 'uppercase',
            color: '#64748b', marginBottom: '10px', textAlign: 'center',
          }}>Live Preview · Selected Pairing</div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CardCanvas
              cardData={sampleData(pairings.find(p => p.slug === selectedSlug) || {})}
              tier="signature"
              watermark={false}
              scale={1}
              side="front"
              fontPairing={pairings.find(p => p.slug === selectedSlug)}
              showStockBadge={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FontPairingPanel;
